import React from 'react';
import { FieldRenderer } from './FieldRenderer';
import type { Asset, Department, FieldConfig, FieldValues, Person } from '../types/models';

interface Props {
  /** Already-effective configs (isEditable resolved by the caller) - this component renders
   * them as-is rather than re-deriving isEditable itself, so the fields it renders and the
   * fields RegisterItemDetail validates on save come from the exact same source. */
  cardFields: FieldConfig[];
  fieldValues: FieldValues;
  isEditing: boolean;
  onChange: (fieldId: string, value: any) => void;
  people: Person[];
  assets: Asset[];
  departments: Department[];
  invalidFieldIds: Set<string>;
  /** Render as a sub-group inside a caller-owned card (eyebrow label, no card chrome of its
   * own) instead of a standalone np-card. Default false = unchanged for every other caller. */
  embedded?: boolean;
  /** Optional read-mode override for a single field's value (e.g. render Status as a tone
   * chip). Return undefined to fall back to the plain text value. */
  renderValue?: (fieldId: string, value: string) => React.ReactNode | undefined;
}

export const IdentityCard: React.FC<Props> = ({ cardFields, fieldValues, isEditing, onChange, people, assets, departments, invalidFieldIds, embedded = false, renderValue }) => {
  // Edit mode is unchanged for both callers - still the real FieldRenderer per field type, with
  // its validation/error highlighting intact. Restyling edit mode to match the new view-mode row
  // layout would mean reimplementing per-field-type inputs and error display outside
  // FieldRenderer, for a state most users pass through quickly rather than read - not worth the
  // risk here; only the read-mode row layout below is new.
  if (isEditing) {
    const editBody = (
      <div className="np-row2">
        {cardFields.map((config) => (
          <FieldRenderer
            key={config.fieldId}
            config={config}
            value={fieldValues[config.fieldId]}
            onChange={onChange}
            people={people}
            assets={assets}
            departments={departments}
            error={invalidFieldIds.has(config.fieldId) ? 'This field is required.' : undefined}
          />
        ))}
      </div>
    );
    if (embedded) {
      return (
        <div className="np-details-group">
          <span className="eyebrow-label">Identity</span>
          {editBody}
        </div>
      );
    }
    return (
      <div className="col-12 mb-4">
        <div className="np-card">
          <div className="np-card-head"><h2>Identity</h2></div>
          <div className="np-card-body">{editBody}</div>
        </div>
      </div>
    );
  }

  // View mode, embedded: single-column label-left/value-right rows (Linear/Attio/Notion's own
  // pattern for a record's properties) instead of the two-column grid below - a fixed label
  // column keeps every value's starting edge aligned regardless of label length.
  if (embedded) {
    return (
      <div className="np-details-group">
        <span className="eyebrow-label">Identity</span>
        {cardFields.map((config) => {
          const value = String(fieldValues[config.fieldId] ?? '').trim();
          return (
            <div key={config.fieldId} className="np-detail-row">
              <span className="np-detail-label">{config.fieldName}</span>
              <span className="np-detail-value np-tabular">{renderValue?.(config.fieldId, value) ?? (value || '—')}</span>
            </div>
          );
        })}
      </div>
    );
  }

  // View mode, non-embedded: unchanged two-column grid (RegisterItemDetail and every other
  // caller besides EvidenceRecordDetail render this branch - embedded is never passed there).
  return (
    <div className="col-12 mb-4">
      <div className="np-card">
        <div className="np-card-head">
          <h2>Identity</h2>
        </div>
        <div className="np-card-body">
          <div className="np-row2">
            {cardFields.map((config) => {
              const value = String(fieldValues[config.fieldId] ?? '').trim();
              return (
                <div key={config.fieldId}>
                  <p
                    className="np-hint mb-1"
                    style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}
                  >
                    {config.fieldName}
                  </p>
                  <p className="mb-0 fw-medium np-tabular">{renderValue?.(config.fieldId, value) ?? (value || '—')}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
